import { Reveal } from "@components/ui";

/* ─── Icons ─── */

function ReceiptIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2L19 4 16 2 13 4 10 2 7 4 4 2z" />
      <path d="M8 10h8M8 14h4" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function MeterIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
    </svg>
  );
}

function FileTextIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

/* ─── Data ─── */

const billingFeatures = [
  { title: "View and Pay Bills", description: "Instant payment confirmation with multiple payment methods", icon: <ReceiptIcon /> },
  { title: "Automated Invoicing", description: "Interactive charts and trend analysis of your usage", icon: <ChartIcon /> },
  { title: "Download Statements", description: "Monthly or yearly usage summaries in PDF format", icon: <DownloadIcon /> },
];

const accountFeatures = [
  { title: "Update Personal Details", description: "Easily update your email, mobile, and address", icon: <UserIcon /> },
  { title: "View Meter Information", description: "Real-time readings for smart meter customers", icon: <MeterIcon /> },
  { title: "Submit Requests", description: "Billing queries, refunds, inspections, move-in/move-out", icon: <FileTextIcon /> },
];

/* ─── Component ─── */

function FeatureCard({ title, description, icon }: { title: string; description: string; icon: React.ReactNode }) {
  return (
    <div className="group flex-1 min-w-0 rounded-[16px] bg-white p-5 md:p-6 lg:p-8 flex flex-col gap-4 border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300">
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] bg-[#f24d2b]/10 text-[#f24d2b] flex items-center justify-center group-hover:bg-[#f24d2b] group-hover:text-white transition-all duration-300">
        {icon}
      </div>
      <h4 className="text-[16px] md:text-[20px] lg:text-[24px] tracking-[-0.04em] font-medium text-[#1c1c1c] font-albert-sans leading-[1.2]">
        {title}
      </h4>
      <p className="text-[13px] md:text-[15px] lg:text-[18px] tracking-[-0.04em] text-[rgba(10,14,46,0.75)] font-albert-sans leading-[1.5]">
        {description}
      </p>
    </div>
  );
}

export function PortalFeaturesSection() {
  return (
    <section className="w-full bg-[#122d63] py-12 md:py-16 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-3 md:gap-4 mb-10 md:mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/[0.12] text-white text-[13px] font-semibold tracking-[-0.02em] uppercase">
              Customer Portal
            </span>
            <h2 className="text-center text-[28px] md:text-[44px] lg:text-[60px] tracking-[-0.04em] font-semibold text-white leading-[1.1]">
              Customer Portal Features
            </h2>
          </div>
        </Reveal>

        {/* Billing & Payments */}
        <Reveal direction="up">
          <div className="mb-6 md:mb-8">
            <h3 className="text-[16px] md:text-[20px] tracking-[-0.04em] font-semibold text-white/60 font-albert-sans mb-4 md:mb-6 uppercase tracking-wider">
              Billing & Payments
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {billingFeatures.map((f) => (
                <FeatureCard key={f.title} {...f} />
              ))}
            </div>
          </div>
        </Reveal>

        {/* Account Management */}
        <Reveal direction="up" delay={100}>
          <div>
            <h3 className="text-[16px] md:text-[20px] tracking-[-0.04em] font-semibold text-white/60 font-albert-sans mb-4 md:mb-6 uppercase tracking-wider">
              Account Management
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {accountFeatures.map((f) => (
                <FeatureCard key={f.title} {...f} />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
